import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useTemplateVersions } from '../_data_sources/useTemplateVersions';
import GridSection from 'components/_layouts/GridSection';
import datetimeHelper from 'helpers/datetimeHelper';

interface VersionHistoryTableProps {
  templateId: number;
  onVersionClick?: (versionId: number) => void;
}

const VersionHistoryTable: React.FC<VersionHistoryTableProps> = ({ templateId, onVersionClick }) => {
  const versionsQuery = useTemplateVersions(templateId, { status: 'Active,Inactive' }, {}, (error: any) => {
    console.error('Error fetching template versions:', error);
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return datetimeHelper.formatDate(dateString);
  };

  const formatVersionNumber = (versionNumber: number, isCurrent: boolean) => {
    return isCurrent ? `V${versionNumber} (current)` : `V${versionNumber}`;
  };

  const getAuthorName = (version: any) => {
    if (version.publishedBy === 'System') {
      return 'System';
    }
    return version.publishedBy ? version.publishedBy.substring(0, 8) + '...' : '--';
  };

  const columns = {
    version: {
      header: t('performanceReporting.templates.details.versionHistory.columns.version'),
      flex: 1,
      renderer: (params: any) => {
        const isCurrent = params.row.status === 'Active' && !params.row.inactivatedDate;
        const versionNumber = params.row.versionNumber;
        return <Typography variant='body2'>{formatVersionNumber(versionNumber, isCurrent)}</Typography>;
      },
    },
    author: {
      header: t('performanceReporting.templates.details.versionHistory.columns.author'),
      flex: 2,
      renderer: (params: any) => (
        <Typography
          variant='body2'
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {getAuthorName(params.row)}
        </Typography>
      ),
    },
    inactivatedDate: {
      header: t('performanceReporting.templates.details.versionHistory.columns.inactivatedDate'),
      flex: 1.5,
      renderer: (params: any) => <Typography variant='body2'>{formatDate(params.row.inactivatedDate)}</Typography>,
    },
  };

  if (versionsQuery.isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (versionsQuery.isError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography
          variant='body2'
          color='error'
        >
          {t('performanceReporting.templates.details.versionHistory.error')}
        </Typography>
      </Box>
    );
  }

  const versions = versionsQuery.data?.pages.flatMap((page) => page.content) || [];
  const totalCount = versionsQuery.data?.pages[0]?.count || 0;

  if (versions.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography
          variant='body2'
          color='text.secondary'
        >
          {t('performanceReporting.templates.details.versionHistory.noVersions')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', minHeight: 0 }}>
      <GridSection
        columns={columns}
        data={versions}
        query={versionsQuery}
        totalCount={totalCount}
        isLoading={versionsQuery.isLoading}
        gridProps={{
          getRowId: (row: any) => row.id.toString(),
        }}
      />
    </Box>
  );
};

export default VersionHistoryTable;

